import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { meApi } from '../services/api';
import { ScopeUser } from '../types';
import { readScope, writeScope } from '../scopeStore';

/**
 * The scope selector's state (MAG-168): who the signed-in user may view as
 * (from core's /users/me/scope) and who they picked. `pick === null` means
 * everyone they may see. The pick is remembered per browser and dropped when
 * a different user signs in. Pages do not read this — the API clients send
 * the pick as a header, and the Shell remounts the screen when it changes.
 */
interface ScopeState {
  loading: boolean;
  /** false when the caller can see nobody but themselves — the control locks. */
  all: boolean;
  users: ScopeUser[];
  pick: string | null;
  setPick: (userId: string | null) => void;
  /** What the control shows: "Everyone", "My team" or the picked person. */
  label: string;
}

const ScopeContext = createContext<ScopeState | undefined>(undefined);

export const ScopeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [all, setAll] = useState(false);
  const [users, setUsers] = useState<ScopeUser[]>([]);
  const [pick, setPickState] = useState<string | null>(() => {
    const s = readScope();
    return s && user && s.by === user.id ? s.user : null;
  });

  useEffect(() => {
    if (!user) { setUsers([]); setAll(false); setLoading(false); return; }
    const stored = readScope();
    if (!stored || stored.by !== user.id) { writeScope({ by: user.id, user: null }); setPickState(null); }
    setLoading(true);
    meApi.scope()
      .then((r) => {
        setUsers(r.users);
        setAll(r.all);
        // A remembered pick that is no longer visible (team changed) is dropped.
        setPickState((p) => (p && !r.users.some((u) => u.id === p) ? null : p));
      })
      .finally(() => setLoading(false));
  }, [user]);

  const setPick = useCallback((userId: string | null) => {
    if (!user) return;
    writeScope({ by: user.id, user: userId });
    setPickState(userId);
  }, [user]);

  const picked = pick ? users.find((u) => u.id === pick) : undefined;
  const label = picked
    ? (picked.display_name || picked.name || 'Someone')
    : user?.role === 'admin' ? 'Everyone' : 'My team';

  return (
    <ScopeContext.Provider value={{ loading, all, users, pick, setPick, label }}>
      {children}
    </ScopeContext.Provider>
  );
};

export const useScope = () => {
  const ctx = useContext(ScopeContext);
  if (!ctx) throw new Error('useScope must be used within ScopeProvider');
  return ctx;
};
